import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '../../../../style-utils/text';
import { palette } from '../../../../style-utils/theme';
import { RightArrow } from '../../../icons/right-arrow';
import {
  BuildingCheckbox,
  BuildingMeta,
  BuildingName,
  BuildingTextStack,
  ButtonsContainer,
  ButtonTitle,
  CancelButton,
  CheckboxSlot,
  CheckTick,
  CheckTickMark,
  ErrorNotice,
  FormBody,
  FormHeader,
  InputContainer,
  ModalBackground,
  PanelHeading,
  PanelNote,
  Pill,
  PortfolioFormCard,
  SaveButton,
  SelectableBuildingRow,
} from './dashboard-portfolios.styled';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import { StatusLabel, scrimMotion, useOverlayMotion } from '../../../../motion';
import {
  addBuildingsToPortfolio,
  HierarchyApiError,
  listPortfolioBuildings,
} from '../../../../api/hierarchy-client';
import {
  Portfolio,
  PortfolioBuildingIndexRow,
} from '../../../../interfaces/hierarchy-interfaces';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';

interface DashboardPortfolioAddBuildingsProps {
  // The destination. Buildings already in it are not offered.
  portfolio: Portfolio;
  onCancel: () => void;
  // The destination plus every portfolio a selected building came from, so the
  // caller can drop the cached detail for both ends of the move.
  onMoved: (affectedPortfolioIds: number[]) => void;
}

// POST /portfolios/:id/buildings runs the whole batch in one transaction and
// refuses more than this in a single call. Exported because the move-OUT modal
// posts to the same endpoint and needs the identical guard — the two must never
// drift apart.
export const BULK_MOVE_MAX = 100;

// One heading plus the buildings under it. Groups keep the order the server
// returned them in rather than being re-sorted, so a building does not jump
// headings between two openings of this dialog.
interface SourceGroup {
  portfolioId: number;
  heading: string;
  buildings: PortfolioBuildingIndexRow[];
}

const buildingLabel = (count: number): string =>
  count === 1 ? '1 building' : `${count} buildings`;

// `status` is free text on properties, so anything that is not an explicit
// 'active' is shown as neutral rather than guessed at.
const buildingTone = (value: string | null): 'success' | 'neutral' =>
  value === 'active' ? 'success' : 'neutral';

const addressLabel = (building: PortfolioBuildingIndexRow): string =>
  [building.address, building.country].filter(Boolean).join(', ') ||
  'No address on file';

const readErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof HierarchyApiError ? error.message : fallback;

export const DashboardPortfolioAddBuildings: React.FC<
  DashboardPortfolioAddBuildingsProps
> = ({ portfolio, onCancel, onMoved }) => {
  // ABSOLUTE PATH, always. Under v7_relativeSplatPath a relative target
  // resolves against the splat route this dashboard is mounted on and lands
  // nowhere — see the app-wide note on that flag.
  const navigate = useNavigate();
  const [rows, setRows] = useState<PortfolioBuildingIndexRow[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const savingRef = useRef(false);

  const dialogRef = useDialogFocus<HTMLFormElement>(true, () => {
    if (!savingRef.current) {
      onCancel();
    }
  });

  // ONE request for the whole org rather than getPortfolio() per portfolio —
  // every row already carries the portfolio holding it.
  const loadBuildings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      setRows(await listPortfolioBuildings());
    } catch (error) {
      setLoadError(
        readErrorMessage(error, 'We could not load your buildings.')
      );
      setRows(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  // Buildings of EVERY status are listed on purpose. An inactive building still
  // occupies a portfolio, still counts toward building_count and still blocks
  // archive and delete — hiding it would strand a portfolio that refuses to
  // archive with nothing visible to move out of it.
  const groups = useMemo<SourceGroup[]>(() => {
    const ordered: SourceGroup[] = [];
    const byPortfolio = new Map<number, SourceGroup>();

    (rows ?? [])
      .filter(row => row.portfolio_id !== portfolio.id)
      .forEach(row => {
        const existing = byPortfolio.get(row.portfolio_id);

        if (existing) {
          existing.buildings.push(row);

          return;
        }

        const group: SourceGroup = {
          portfolioId: row.portfolio_id,
          heading: `Currently in ${row.portfolio_name || 'another portfolio'}`,
          buildings: [row],
        };

        byPortfolio.set(row.portfolio_id, group);
        ordered.push(group);
      });

    return ordered;
  }, [portfolio.id, rows]);

  const hasCandidates = groups.length > 0;
  const selectedCount = selectedIds.length;
  const isOverCap = selectedCount > BULK_MOVE_MAX;

  const toggleSelected = (buildingId: number) => {
    setSelectedIds(current =>
      current.includes(buildingId)
        ? current.filter(id => id !== buildingId)
        : [...current, buildingId]
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (savingRef.current || selectedCount === 0 || isOverCap) {
      return;
    }

    // Read the sources BEFORE the move, while the rows still say where each
    // building came from — afterwards every one of them says this portfolio.
    const sourceIds = Array.from(
      new Set(
        (rows ?? [])
          .filter(row => selectedIds.includes(row.id))
          .map(row => row.portfolio_id)
      )
    );

    setSubmitError(null);
    savingRef.current = true;
    setIsSaving(true);

    try {
      await addBuildingsToPortfolio(portfolio.id, selectedIds);

      savingRef.current = false;
      onMoved([portfolio.id, ...sourceIds]);
    } catch (error) {
      setSubmitError(
        readErrorMessage(error, 'Something went wrong. Please try again.')
      );
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return <ListSkeleton rows={3} label="buildings" />;
    }

    if (loadError) {
      return <ErrorNotice message={loadError} onRetry={loadBuildings} />;
    }

    if (!hasCandidates) {
      return (
        <>
          {/* ── A TRUE SENTENCE THAT WAS STILL A DEAD END ──────────────────
              "Add buildings…" opens a MOVE: a building belongs to exactly one
              portfolio, so this dialog can only offer the ones held elsewhere.
              In an organisation with a single portfolio that set is always
              empty, and the note said so and stopped — so the one button
              labelled "Add buildings" answered "there are none" and offered
              nothing, which reads as a broken feature rather than as the
              difference between moving a building and creating one.
              Naming the next step is the whole fix. */}
          <PanelNote>
            Every building in your organisation is already in {portfolio.name},
            so there is nothing to move in. To add a building to Lobbero, create
            it first — new buildings land in your default portfolio and can be
            moved from here afterwards.
          </PanelNote>
          <ButtonsContainer>
            <SaveButton
              type="button"
              onClick={() => navigate('/dashboard/buildings/add-new-property')}
            >
              <ButtonTitle>Create a building</ButtonTitle>
            </SaveButton>
          </ButtonsContainer>
        </>
      );
    }

    return (
      <>
        {groups.map(group => (
          <InputContainer key={group.portfolioId}>
            <PanelHeading>{group.heading}</PanelHeading>
            {group.buildings.map(building => {
              const isSelected = selectedIds.includes(building.id);

              return (
                <SelectableBuildingRow
                  key={building.id}
                  isSelected={isSelected}
                >
                  <CheckboxSlot>
                    <BuildingCheckbox
                      type="checkbox"
                      checked={isSelected}
                      disabled={isSaving}
                      aria-label={`Move ${building.property_name} into ${portfolio.name}`}
                      onChange={() => toggleSelected(building.id)}
                    />
                    {isSelected && (
                      <CheckTick>
                        <CheckTickMark />
                      </CheckTick>
                    )}
                  </CheckboxSlot>
                  <BuildingTextStack>
                    <BuildingName>{building.property_name}</BuildingName>
                    <BuildingMeta>{addressLabel(building)}</BuildingMeta>
                  </BuildingTextStack>
                  <Pill tone={buildingTone(building.status)}>
                    {building.status === 'active' ? 'Active' : 'Inactive'}
                  </Pill>
                </SelectableBuildingRow>
              );
            })}
          </InputContainer>
        ))}
      </>
    );
  };

  const modalMotion = useOverlayMotion('modal');

  return (
    <ModalPortal>
      <ModalBackground
        {...scrimMotion}
        id="portfolio-add-buildings-background"
        onMouseDown={() => {
          if (!savingRef.current) {
            onCancel();
          }
        }}
      >
        <PortfolioFormCard
          {...modalMotion}
          ref={dialogRef}
          tabIndex={-1}
          id="portfolio-add-buildings-form"
          onMouseDown={event => event.stopPropagation()}
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="portfolio-add-buildings-heading"
          noValidate
        >
          <FormHeader>
            <Text
              id="portfolio-add-buildings-heading"
              mt={0}
              mb={0}
              fontSize={20}
              fontWeight={700}
              color={palette.ink}
            >
              Add buildings to {portfolio.name}
            </Text>
            <Text mt={0} mb={0} fontSize={13} color={palette.slate}>
              A building belongs to exactly one portfolio, so anything you pick
              here moves out of the portfolio holding it now.
            </Text>
          </FormHeader>

          <FormBody>
            <ErrorNotice message={submitError} />

            {renderBody()}

            {isOverCap && (
              <PanelNote>
                A single move carries at most {BULK_MOVE_MAX} buildings. Deselect{' '}
                {buildingLabel(selectedCount - BULK_MOVE_MAX)} to continue.
              </PanelNote>
            )}
          </FormBody>

          <ButtonsContainer>
            <CancelButton type="button" disabled={isSaving} onClick={onCancel}>
              <ButtonTitle>Cancel</ButtonTitle>
            </CancelButton>
            <SaveButton
              type="submit"
              disabled={isSaving || selectedCount === 0 || isOverCap}
              aria-busy={isSaving}
              title={
                isOverCap
                  ? `A single move carries at most ${BULK_MOVE_MAX} buildings.`
                  : undefined
              }
            >
              <StatusLabel
                state={isSaving ? 'busy' : 'idle'}
                labels={{
                  idle: (
                    <>
                      <ButtonTitle>
                        {selectedCount === 0
                          ? 'Move buildings here'
                          : `Move ${buildingLabel(selectedCount)} here`}
                      </ButtonTitle>
                      <RightArrow />
                    </>
                  ),
                  busy: <ButtonTitle>Moving…</ButtonTitle>,
                }}
              />
            </SaveButton>
          </ButtonsContainer>
        </PortfolioFormCard>
      </ModalBackground>
    </ModalPortal>
  );
};
